import React, { useState, useRef, useEffect, useContext } from 'react';
import MicRecorder from 'mic-recorder-to-mp3';
import { CheckMicAndPermissionsStyles } from '../Style';
import { AnimatePresence } from "motion/react"
import { PerformAssessmentContext } from '../PerformAssessmentContext';
import { Button } from 'components/Form'

import Recording from './Recording';
import Success from './Success'
import Error from './Error'
import Idle from './Idle'
import NoPermissions from './NoPermissions';
import PermissionBlocked from './PermissionBlocked';
import { ChevronLeft } from '@mui/icons-material';


const Mp3Recorder = new MicRecorder({ bitRate: 128 });

const MicCalibration = () => {
    // מצבי המסך השונים
    const [step, setStep] = useState('idle'); // 'idle' | 'testing_mic' | 'analyzing' | 'success' | 'error_general' | 'error_permission' | 'error_permission_blocked'
    const [errorMessage, setErrorMessage] = useState('');

    const { setCurrentStep, isMaleGender, isHighGrade, isTeacherCheck } = useContext(PerformAssessmentContext)

    // רפרנסים לשמירת החומרה והטיימרים
    const audioContextRef = useRef(null);
    const checkIntervalRef = useRef(null);
    const canvasRef = useRef(null);
    const animationIdRef = useRef(null);

    // ניקוי זיכרון וחומרה
    const cleanup = async () => {
        if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);

        if (animationIdRef.current != null) {
            cancelAnimationFrame(animationIdRef.current);
            animationIdRef.current = null;
        }

        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close().catch(console.error);
            audioContextRef.current = null;
        }

        try {
            await Mp3Recorder?.stop();
        } catch (e) {
            // מתעלמים משגיאות עצירה למקרה שהספרייה כבר נעצרה
        }
    };

    // מנקה זיכרון ביציאה מהמסך
    useEffect(() => {
        return () => cleanup();
    }, []);

    // פונקציית עזר למקרה של כישלון באחת הבדיקות
    // (ברירת המחדל היא שגיאה כללית)
    // teacherMsg - נוסח למורה בפופאפ בדיקת השמע (לשון נקבה)
    const failTest = (msg, errorStep = 'error_general', teacherMsg) => {
        cleanup();
        setStep(errorStep); // מגדיר את הסטטוס המדויק של השגיאה
        setErrorMessage(isTeacherCheck && teacherMsg ? teacherMsg : msg);
    };

    const onTestPassed = () => {
        setCurrentStep(2)
    }

    // בדיקה חוזרת אחרי חסימת מיקרופון ,
    // אם אשרו גישה מיעבור שוב לבדיקת שמע אחרי יעבור להסבר איך לאפשר
    const recheckPermission = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(track => track.stop());
            setStep('idle');
        } catch (error) {
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
                setStep('error_permission_blocked');
            else
                setStep('idle'); 
        }
    }

    // מצייר את פסי עוצמת הקול על ה-canvas בזמן אמת, לפי נתוני התדרים מה-analyser
    const draw = (analyser) => {
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        if (!context) return;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);

        context.clearRect(0, 0, canvas.width, canvas.height);

        const barWidth = (canvas.width / bufferLength) * 2.5;
        let barHeight;
        let x = 0;

        for (let i = 0; i < bufferLength; i++) {
            barHeight = dataArray[i] / 4;
            context.fillStyle = '#8BBC01';
            context.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
            x += barWidth + 1;
        }

        animationIdRef.current = requestAnimationFrame(() => draw(analyser));
    };

    const handleStartTest = async () => {
        setErrorMessage('');

        // ==========================================
        // שלב 1: בדיקות מקדימות והרשאות
        // ==========================================

        // א. בדיקה שהדפדפן תומך בהקלטה
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            failTest('הדפדפן שלך ישן מדי או לא תומך בהקלטת שמע. אנא נסה מדפדפן כרום מעודכן.', 'error_general',
                'הדפדפן לא תומך בהקלטת שמע\nנסי מדפדפן כרום מעודכן');
            return;
        }

        // ב. בדיקת הרשאות וחיבור פיזי מפורשת
        try {
            const testStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // סוגרים מיד את הזרם הזה כי הספרייה תפתח אחד משלה תכף
            testStream.getTracks().forEach(track => track.stop());
        } catch (error) {
            console.error("Explicit Permission Error:", error);
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                // כאן אנחנו שולחים אותו ל-step הייעודי של התמונה וההסבר!
                failTest('הדפדפן חוסם את הגישה למיקרופון.', 'error_permission');
            } else if (error.name === 'NotFoundError') {
                failTest('לא מצאנו מיקרופון מחובר. ודא שהמיקרופון מחובר למחשב.', 'error_general',
                    'לא מצאנו מיקרופון מחובר\nוודאי שהמיקרופון מחובר למחשב ותנסי שוב');
            } else {
                failTest('תקלת חומרה כללית בקריאת המיקרופון שלך.', 'error_general',
                    'יש תקלה בקריאת המיקרופון\nנסי שוב');
            }
            return;
        }

        // ==========================================
        // שלב 2: תחילת ההקלטה ואיסוף נתונים
        // ==========================================
        setStep('testing_mic');

        try {
            // מפעילים את הספרייה שלך
            const stream = await Mp3Recorder.start();

            // מחברים את כלי הניתוח
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            const analyser = audioContextRef.current.createAnalyser();
            analyser.fftSize = 256;
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyser);

            draw(analyser);

            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            // משתנים למעקב אחרי איכות הסאונד
            let totalFrames = 0;
            let activeFrames = 0;
            let clippingFrames = 0; // סופר כמה פריימים היו מוגזמים
            let minRms = 1.0; // מתחילים גבוה כדי למצוא את המינימום האמיתי (לרעש סטטי)

            // דוגמים את האודיו כל 50 מילישניות
            checkIntervalRef.current = setInterval(() => {
                analyser.getByteTimeDomainData(dataArray);
                let sum = 0;

                for (let i = 0; i < bufferLength; i++) {
                    let normalized = (dataArray[i] - 128) / 128;
                    sum += normalized * normalized;

                    // זיהוי צרימה קיצונית בלבד (0.99) וספירתה
                    if (Math.abs(normalized) > 0.99) clippingFrames++;
                }

                let rms = Math.sqrt(sum / bufferLength);

                // עדכון הרגע הכי שקט בהקלטה (לצורך זיהוי רעש סטטי)
                if (rms < minRms) minRms = rms;

                // עדכון פריימים פעילים (קול אנושי)
                if (rms > 0.01) activeFrames++;

                totalFrames++;
            }, 50);

            // ממתינים 5 שניות בדיוק לביצוע הבדיקה
            await new Promise(resolve => setTimeout(resolve, 5000));

            // ==========================================
            // שלב 3: פענוח וניתוח איכות ההקלטה
            // ==========================================
            setStep('analyzing');
            clearInterval(checkIntervalRef.current);

            // עוצרים את ההקלטה ומקבלים את הקובץ
            const result = await Mp3Recorder.stop().getMp3();
            const blob = result[1];

            // ג. בדיקת קידוד: האם נוצר קובץ אודיו אמיתי?
            if (!blob || blob.size < 500) {
                failTest('המיקרופון פועל, אבל הדפדפן נכשל ביצירת הקובץ. נסה לרענן את העמוד.', 'error_general',
                    'המיקרופון פועל, אבל ההקלטה לא נשמרה\nנסי לרענן את העמוד');
                return;
            }

            // ד. בדיקת רעש סטטי: האם הרגע הכי שקט עדיין היה רועש מדי?
            // (משמעות: יש זמזום חשמלי קבוע מהכרטיס קול או המיקרופון)
            if (minRms > 0.06) {
                failTest('זיהינו זמזום או רעש סטטי קבוע ברקע. נסה לבדוק את חיבור המיקרופון או להחליף אותו.', 'error_general',
                    'נשמע רעש קבוע ברקע\nוודאי שהמיקרופון מחובר היטב ותנסי שוב');
                return;
            }

            // ה. בדיקת שקט: האם הילד באמת דיבר? 
            // כל פריים נדגם כל 50 מילישניות. 10 פריימים = חצי שנייה של קול מצטבר.
            // אנחנו בודקים האם הצטברה לפחות חצי שנייה אחת של סאונד במהלך ה-5 שניות.
            if (activeFrames < 10) {
                failTest('המיקרופון מחובר, אבל הקול היה חלש או קצר מדי. נסה להגיד ברור "אני מוכן!" כשההקלטה מתחילה.', 'error_general',
                    'נשמע שהקול חלש\nוודאי שהמיקרופון נמצא צמוד לפה ותנסי שוב');
                return;
            }

            // ו. בדיקת עיוות וצרחות קיצוניות
            // נכשיל רק אם הילד צרח בשיא העוצמה במשך יותר מ-2 שניות (40 פריימים מתוך 100)
            if (clippingFrames > 40) {
                failTest('הקול היה חזק וצורם מדי לאורך זמן. נסה לדבר קצת יותר חלש או להתרחק מעט מהמיקרופון.', 'error_general',
                    'נשמע שהקול חזק מדי\nנסי לדבר קצת יותר חלש או להרחיק את המיקרופון');
                return;
            }

            // ==========================================
            // הכל עבר בהצלחה!
            // ==========================================
            cleanup();
            setStep('success');

        } catch (err) {
            console.error(err);
            failTest('התרחשה שגיאה טכנית בזמן ההקלטה. נסה שוב.', 'error_general',
                'התרחשה תקלה בזמן ההקלטה\nנסי שוב');
        }
    };

    useEffect(() => {
        // בודקים אם הדפדפן תומך ב-Permissions API
        if (navigator.permissions && navigator.permissions.query) {
            navigator.permissions.query({ name: 'microphone' })
                .then((permissionStatus) => {
                    // מאזינים לשינוי סטטוס
                    permissionStatus.onchange = () => {
                        if (permissionStatus.state === 'granted') {
                            // הלקוח אישר את המיקרופון דרך המנעול!
                            // נחזיר את המסך למצב ההתחלתי כדי שיוכל ללחוץ "התחל בדיקה" מחדש
                            setStep('idle');
                            setErrorMessage('');
                        }
                    };
                })
                .catch(console.error); // התעלמות מדפדפנים ישנים שלא תומכים בזה
        }

        return () => cleanup(); // זה ה-cleanup שכבר יש לנו
    }, []);

    // useEffect(() => {
    //     onTestPassed()
    // }, [])

    const commonState = {
        isMaleGender: isMaleGender,
        isHighGrade: isHighGrade,
        isTeacherCheck: isTeacherCheck
    }

    return (
        <CheckMicAndPermissionsStyles>
            <AnimatePresence mode="wait">
                {step === 'idle' && (
                    <Idle {...commonState} start={handleStartTest} />
                )}

                {step === 'testing_mic' && (
                    <Recording {...commonState} canvasRef={canvasRef} />
                )}

                {step === 'error_general' && (
                    <Error {...commonState} error={errorMessage} checkAgain={() => setStep('idle')} start={handleStartTest} />
                )}

                {step === 'error_permission' && (
                    <NoPermissions {...commonState} checkAgain={recheckPermission} />
                )}

                {step === 'error_permission_blocked' && (
                    <PermissionBlocked {...commonState} checkAgain={recheckPermission} />
                )}

                {step === 'success' && (
                    <Success {...commonState} onTestPassed={onTestPassed} />
                )}
                {!isTeacherCheck &&
                    <div className='skip-btn'>
                        <Button color="dark" action={onTestPassed}>
                            {"דלג"}
                            <ChevronLeft />
                        </Button>
                    </div>}
            </AnimatePresence>
        </CheckMicAndPermissionsStyles>
    );
};

export default MicCalibration;
